import { useFormikContext } from 'formik';
import React, { useMemo, useState, useEffect } from 'react';
import Select from '../../../BaseComponents/Select';
import { UserGenderEnum } from '../../../../types/apiGeneratedTypes';
import TextInput from '../../../BaseComponents/TextInput';
import Button from '../../../BaseComponents/Button';
import { AgesGenderValuesBlock } from './AgesGenderValuesBlock';
import { ReactComponent as PlusIcon } from '../../../../assets/icons/AddTestButton.svg';

interface GenderValuesBlockProps {
  dataIndex: number;
}

export const GenderValuesBlock: React.FC<GenderValuesBlockProps> = ({
  dataIndex
}) => {
  const { setFieldValue, values } = useFormikContext<any>();
  const [isInfiniteLowTo, setIsInfiniteLowTo] = useState<boolean>(false);
  const [isInfiniteBelowOptimalTo, setIsInfiniteBelowOptimalTo] = useState<boolean>(false);
  const [isInfiniteOptimalTo, setIsInfiniteOptimalTo] = useState<boolean>(false);
  const [isInfiniteAboveOptimalTo, setIsInfiniteAboveOptimalTo] = useState<boolean>(false);
  const [isInfiniteHighTo, setIsInfiniteHighTo] = useState<boolean>(false);
  const genderData = values.genderData[dataIndex];
  const lowToInfinity = genderData?.low_to_infinity;
  const belowOptimalToInfinity = genderData?.below_optimal_to_infinity;
  const optimalToInfinity = genderData?.optimal_to_infinity;
  const aboveOptimalToInfinity = genderData?.above_optimal_to_infinity;
  const highToInfinity = genderData?.high_to_infinity;

  useEffect(() => {
    setIsInfiniteLowTo(lowToInfinity === true);
    setIsInfiniteBelowOptimalTo(belowOptimalToInfinity === true);
    setIsInfiniteOptimalTo(optimalToInfinity === true);
    setIsInfiniteAboveOptimalTo(aboveOptimalToInfinity === true);
    setIsInfiniteHighTo(highToInfinity === true);
  }, [
    lowToInfinity,
    belowOptimalToInfinity,
    optimalToInfinity,
    aboveOptimalToInfinity,
    highToInfinity,
  ]);
  const onAddGenderBlock = () => {
    setFieldValue(`genderData`, [
      ...values.genderData,
      {
        gender:
          values.genderData[dataIndex].gender.key === UserGenderEnum.Male
            ? { key: UserGenderEnum.Female, value: 'Female' }
            : { key: UserGenderEnum.Male, value: 'Male' },
        data_with_age: [
          {
            age_from: '',
            age_to: '',
            low_from: '',
            low_to: '',
            low_to_infinity: false,
            below_optimal_from: '',
            below_optimal_to: '',
            below_optimal_to_infinity: false,
            above_optimal_from: '',
            above_optimal_to: '',
            above_optimal_to_infinity: false,
            optimal_from: '',
            optimal_to: '',
            optimal_to_infinity: false,
            high_from: '',
            high_to: '',
            high_to_infinity: false
          }
        ],
        low_from: '',
        low_to: '',
        low_to_infinity: false,
        high_from: '',
        high_to: '',
        high_to_infinity: false,
        below_optimal_from: '',
        below_optimal_to: '',
        below_optimal_to_infinity: false,
        above_optimal_from: '',
        above_optimal_to: '',
        above_optimal_to_infinity: false,
        optimal_from: '',
        optimal_to: '',
        optimal_to_infinity: false
      }
    ]);
  };

  const addAgeBlock = () => {
    setFieldValue(`genderData[${dataIndex}].data_with_age`, [
      ...values.genderData[dataIndex].data_with_age,
      {
        age_from: '',
        age_to: '',
        low_from: '',
        low_to: '',
        low_to_infinity: false,
        high_from: '',
        high_to: '',
        high_to_infinity: false,
        below_optimal_from: '',
        below_optimal_to: '',
        below_optimal_to_infinity: false,
        above_optimal_from: '',
        above_optimal_to: '',
        above_optimal_to_infinity: false,
        optimal_from: '',
        optimal_to: '',
        optimal_to_infinity: false
      }
    ]);
  };

  const onDeleteGenderBlock = () => {
    setFieldValue(`genderData`, [
      ...values.genderData.slice(0, dataIndex),
      ...values.genderData.slice(dataIndex + 1)
    ]);
  };

  const onCriticalLowFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const criticalLowToValue = e.target.value;
    setFieldValue(
      `genderData[${dataIndex}].low_from`,
      criticalLowToValue
    );
  };
  const onCriticalLowToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const criticalLowToValue = e.target.value;
    setFieldValue(
      `genderData[${dataIndex}].low_to`,
      criticalLowToValue
    );
  };

  const onLowFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const lowFromValue = e.target.value;
    setFieldValue(`genderData[${dataIndex}].below_optimal_from`, lowFromValue);
  };

  const onLowToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const lowToValue = e.target.value;
    setFieldValue(`genderData[${dataIndex}].below_optimal_to`, lowToValue);
  };

  const onOptimalFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const optimalFromValue = e.target.value;
    setFieldValue(`genderData[${dataIndex}].optimal_from`, optimalFromValue);
  };

  const onOptimalToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const optimalToValue = e.target.value;
    setFieldValue(`genderData[${dataIndex}].optimal_to`, optimalToValue);
  };

  const onHighFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const highFromValue = e.target.value;
    setFieldValue(`genderData[${dataIndex}].above_optimal_from`, highFromValue);
  };

  const onHighToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const highToValue = e.target.value;
    setFieldValue(`genderData[${dataIndex}].above_optimal_to`, highToValue);
  };

  const onCriticalHighFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const criticalHighFromValue = e.target.value;
    setFieldValue(
      `genderData[${dataIndex}].high_from`,
      criticalHighFromValue
    );
  };

  const onCriticalHighToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFieldValue(`genderData[${dataIndex}].high_to`, e.target.value);
  };

  const onInfiniteLowToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setIsInfiniteLowTo(isChecked);
    if (isChecked) {
      setFieldValue(`genderData[${dataIndex}].low_to`, '∞');
      setFieldValue(`genderData[${dataIndex}].below_optimal_from`, 'NA');
      setFieldValue(`genderData[${dataIndex}].below_optimal_to`, 'NA');
      setFieldValue(`genderData[${dataIndex}].optimal_from`, 'NA');
      setFieldValue(`genderData[${dataIndex}].optimal_to`, 'NA');
      setFieldValue(`genderData[${dataIndex}].above_optimal_from`, 'NA');
      setFieldValue(`genderData[${dataIndex}].above_optimal_to`, 'NA');
      setFieldValue(`genderData[${dataIndex}].high_from`, 'NA');
      setFieldValue(`genderData[${dataIndex}].high_to`, 'NA');
      setIsInfiniteBelowOptimalTo(false);
      setIsInfiniteOptimalTo(false);
      setIsInfiniteAboveOptimalTo(false);
      setIsInfiniteHighTo(false);
      setFieldValue(`genderData[${dataIndex}].below_optimal_to_infinity`, false);
      setFieldValue(`genderData[${dataIndex}].optimal_to_infinity`, false);
      setFieldValue(`genderData[${dataIndex}].above_optimal_to_infinity`, false);
      setFieldValue(`genderData[${dataIndex}].high_to_infinity`, false);
    } else {
      setFieldValue(`genderData[${dataIndex}].low_to`, '');
      if (values.genderData[dataIndex].below_optimal_from === 'NA') setFieldValue(`genderData[${dataIndex}].below_optimal_from`, '');
      if (values.genderData[dataIndex].below_optimal_to === 'NA') setFieldValue(`genderData[${dataIndex}].below_optimal_to`, '');
      if (values.genderData[dataIndex].optimal_from === 'NA') setFieldValue(`genderData[${dataIndex}].optimal_from`, '');
      if (values.genderData[dataIndex].optimal_to === 'NA') setFieldValue(`genderData[${dataIndex}].optimal_to`, '');
      if (values.genderData[dataIndex].above_optimal_from === 'NA') setFieldValue(`genderData[${dataIndex}].above_optimal_from`, '');
      if (values.genderData[dataIndex].above_optimal_to === 'NA') setFieldValue(`genderData[${dataIndex}].above_optimal_to`, '');
      if (values.genderData[dataIndex].high_from === 'NA') setFieldValue(`genderData[${dataIndex}].high_from`, '');
      if (values.genderData[dataIndex].high_to === 'NA') setFieldValue(`genderData[${dataIndex}].high_to`, '');
    }
    setFieldValue(`genderData[${dataIndex}].low_to_infinity`, isChecked);
  };

  const onInfiniteBelowOptimalToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setIsInfiniteBelowOptimalTo(isChecked);
    if (isChecked) {
      setFieldValue(`genderData[${dataIndex}].below_optimal_to`, '∞');
      setFieldValue(`genderData[${dataIndex}].optimal_from`, 'NA');
      setFieldValue(`genderData[${dataIndex}].optimal_to`, 'NA');
      setFieldValue(`genderData[${dataIndex}].above_optimal_from`, 'NA');
      setFieldValue(`genderData[${dataIndex}].above_optimal_to`, 'NA');
      setFieldValue(`genderData[${dataIndex}].high_from`, 'NA');
      setFieldValue(`genderData[${dataIndex}].high_to`, 'NA');
      setIsInfiniteOptimalTo(false);
      setIsInfiniteAboveOptimalTo(false);
      setIsInfiniteHighTo(false);
      setFieldValue(`genderData[${dataIndex}].optimal_to_infinity`, false);
      setFieldValue(`genderData[${dataIndex}].above_optimal_to_infinity`, false);
      setFieldValue(`genderData[${dataIndex}].high_to_infinity`, false);
    } else {
      setFieldValue(`genderData[${dataIndex}].below_optimal_to`, '');
      if (values.genderData[dataIndex].optimal_from === 'NA') setFieldValue(`genderData[${dataIndex}].optimal_from`, '');
      if (values.genderData[dataIndex].optimal_to === 'NA') setFieldValue(`genderData[${dataIndex}].optimal_to`, '');
      if (values.genderData[dataIndex].above_optimal_from === 'NA') setFieldValue(`genderData[${dataIndex}].above_optimal_from`, '');
      if (values.genderData[dataIndex].above_optimal_to === 'NA') setFieldValue(`genderData[${dataIndex}].above_optimal_to`, '');
      if (values.genderData[dataIndex].high_from === 'NA') setFieldValue(`genderData[${dataIndex}].high_from`, '');
      if (values.genderData[dataIndex].high_to === 'NA') setFieldValue(`genderData[${dataIndex}].high_to`, '');
    }
    setFieldValue(`genderData[${dataIndex}].below_optimal_to_infinity`, isChecked);
  };

  const onInfiniteOptimalToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setIsInfiniteOptimalTo(isChecked);
    if (isChecked) {
      setFieldValue(`genderData[${dataIndex}].optimal_to`, '∞');
      setFieldValue(`genderData[${dataIndex}].above_optimal_from`, 'NA');
      setFieldValue(`genderData[${dataIndex}].above_optimal_to`, 'NA');
      setFieldValue(`genderData[${dataIndex}].high_from`, 'NA');
      setFieldValue(`genderData[${dataIndex}].high_to`, 'NA');
      setIsInfiniteAboveOptimalTo(false);
      setIsInfiniteHighTo(false);
      setFieldValue(`genderData[${dataIndex}].above_optimal_to_infinity`, false);
      setFieldValue(`genderData[${dataIndex}].high_to_infinity`, false);
    } else {
      setFieldValue(`genderData[${dataIndex}].optimal_to`, '');
      if (values.genderData[dataIndex].above_optimal_from === 'NA') setFieldValue(`genderData[${dataIndex}].above_optimal_from`, '');
      if (values.genderData[dataIndex].above_optimal_to === 'NA') setFieldValue(`genderData[${dataIndex}].above_optimal_to`, '');
      if (values.genderData[dataIndex].high_from === 'NA') setFieldValue(`genderData[${dataIndex}].high_from`, '');
      if (values.genderData[dataIndex].high_to === 'NA') setFieldValue(`genderData[${dataIndex}].high_to`, '');
    }
    setFieldValue(`genderData[${dataIndex}].optimal_to_infinity`, isChecked);
  };

  const onInfiniteAboveOptimalToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setIsInfiniteAboveOptimalTo(isChecked);
    if (isChecked) {
      setFieldValue(`genderData[${dataIndex}].above_optimal_to`, '∞');
      setFieldValue(`genderData[${dataIndex}].high_from`, 'NA');
      setFieldValue(`genderData[${dataIndex}].high_to`, 'NA');
      setIsInfiniteHighTo(false);
      setFieldValue(`genderData[${dataIndex}].high_to_infinity`, false);
    } else {
      setFieldValue(`genderData[${dataIndex}].above_optimal_to`, '');
      if (values.genderData[dataIndex].high_from === 'NA') setFieldValue(`genderData[${dataIndex}].high_from`, '');
      if (values.genderData[dataIndex].high_to === 'NA') setFieldValue(`genderData[${dataIndex}].high_to`, '');
    }
    setFieldValue(`genderData[${dataIndex}].above_optimal_to_infinity`, isChecked);
  };

  const onInfiniteHighToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setIsInfiniteHighTo(isChecked);
    if (isChecked) {
      setFieldValue(`genderData[${dataIndex}].high_to`, '∞');
    } else {
      setFieldValue(`genderData[${dataIndex}].high_to`, '');
    }
    setFieldValue(`genderData[${dataIndex}].high_to_infinity`, isChecked);
  };

  const onDisableRange = (
    e: React.ChangeEvent<HTMLInputElement>,
    keys: string[]
  ) => {
    const isDisabling = e.target.checked;
    const val = isDisabling ? 'NA' : '';

    let hadInfinity = false;
    if (keys.includes('low_to') && isInfiniteLowTo) hadInfinity = true;
    if (keys.includes('below_optimal_to') && isInfiniteBelowOptimalTo) hadInfinity = true;
    if (keys.includes('optimal_to') && isInfiniteOptimalTo) hadInfinity = true;
    if (keys.includes('above_optimal_to') && isInfiniteAboveOptimalTo) hadInfinity = true;
    if (keys.includes('high_to') && isInfiniteHighTo) hadInfinity = true;

    if (isDisabling && hadInfinity) {
      if (!keys.includes('low_to') && values.genderData[dataIndex].low_from === 'NA') {
        setFieldValue(`genderData[${dataIndex}].low_from`, '');
        setFieldValue(`genderData[${dataIndex}].low_to`, '');
      }
      if (!keys.includes('below_optimal_to') && values.genderData[dataIndex].below_optimal_from === 'NA') {
        setFieldValue(`genderData[${dataIndex}].below_optimal_from`, '');
        setFieldValue(`genderData[${dataIndex}].below_optimal_to`, '');
      }
      if (!keys.includes('optimal_to') && values.genderData[dataIndex].optimal_from === 'NA') {
        setFieldValue(`genderData[${dataIndex}].optimal_from`, '');
        setFieldValue(`genderData[${dataIndex}].optimal_to`, '');
      }
      if (!keys.includes('above_optimal_to') && values.genderData[dataIndex].above_optimal_from === 'NA') {
        setFieldValue(`genderData[${dataIndex}].above_optimal_from`, '');
        setFieldValue(`genderData[${dataIndex}].above_optimal_to`, '');
      }
      if (!keys.includes('high_to') && values.genderData[dataIndex].high_from === 'NA') {
        setFieldValue(`genderData[${dataIndex}].high_from`, '');
        setFieldValue(`genderData[${dataIndex}].high_to`, '');
      }
    }

    setFieldValue(`genderData[${dataIndex}].${keys[0]}`, val);
    setFieldValue(`genderData[${dataIndex}].${keys[1]}`, val);

    if (keys.includes('low_to')) {
      setIsInfiniteLowTo(false);
      setFieldValue(`genderData[${dataIndex}].low_to_infinity`, false);
    }
    if (keys.includes('below_optimal_to')) {
      setIsInfiniteBelowOptimalTo(false);
      setFieldValue(`genderData[${dataIndex}].below_optimal_to_infinity`, false);
    }
    if (keys.includes('optimal_to')) {
      setIsInfiniteOptimalTo(false);
      setFieldValue(`genderData[${dataIndex}].optimal_to_infinity`, false);
    }
    if (keys.includes('above_optimal_to')) {
      setIsInfiniteAboveOptimalTo(false);
      setFieldValue(`genderData[${dataIndex}].above_optimal_to_infinity`, false);
    }
    if (keys.includes('high_to')) {
      setIsInfiniteHighTo(false);
      setFieldValue(`genderData[${dataIndex}].high_to`, val);
      setFieldValue(`genderData[${dataIndex}].high_to_infinity`, false);
    }
  };

  const isCriticalLowFromError = useMemo(() => {
    const criticalLowFrom = values.genderData[dataIndex].low_from;
    const criticalLowTo = values.genderData[dataIndex].low_to;
    return (
      criticalLowFrom !== '' &&
      criticalLowTo !== '' &&
      Number(criticalLowFrom) >= Number(criticalLowTo)
    );
  }, [dataIndex, values.genderData]);

  const isCriticalLowToError = useMemo(() => {
    const criticalLowTo = values.genderData[dataIndex].low_to;
    const criticalLowFrom = values.genderData[dataIndex].low_from;
    return (
      criticalLowTo !== '' &&
      Number(criticalLowTo) <= Number(criticalLowFrom)
    );
  }, [dataIndex, values.genderData]);

  const isLowFromError = useMemo(() => {
    const belowOptimalFrom = values.genderData[dataIndex].below_optimal_from;
    const belowOptimalTo = values.genderData[dataIndex].below_optimal_to;
    return (
      belowOptimalFrom !== '' &&
      belowOptimalTo !== '' &&
      Number(belowOptimalFrom) >= Number(belowOptimalTo)
    );
  }, [dataIndex, values.genderData]);

  const isLowToError = useMemo(() => {
    const belowOptimalTo = values.genderData[dataIndex].below_optimal_to;
    const belowOptimalFrom = values.genderData[dataIndex].below_optimal_from;
    return belowOptimalTo !== '' && belowOptimalFrom !== '' && Number(belowOptimalTo) <= Number(belowOptimalFrom);
  }, [dataIndex, values.genderData]);

  const isOptimalFromError = useMemo(() => {
    const optimalFrom = values.genderData[dataIndex].optimal_from;
    const belowOptimalTo = values.genderData[dataIndex].below_optimal_to;
    return (
      optimalFrom !== '' && belowOptimalTo !== '' && Number(optimalFrom) <= Number(belowOptimalTo)
    );
  }, [dataIndex, values.genderData]);

  const isOptimalToError = useMemo(() => {
    const optimalTo = values.genderData[dataIndex].optimal_to;
    const optimalFrom = values.genderData[dataIndex].optimal_from;
    return (
      optimalTo !== '' &&
      optimalFrom !== '' &&
      Number(optimalTo) <= Number(optimalFrom)
    );
  }, [dataIndex, values.genderData]);

  const isHighFromError = useMemo(() => {
    const aboveOptimalFrom = values.genderData[dataIndex].above_optimal_from;
    const optimalTo = values.genderData[dataIndex].optimal_to;
    return (
      aboveOptimalFrom !== '' &&
      optimalTo !== '' &&
      Number(aboveOptimalFrom) <= Number(optimalTo)
    );
  }, [dataIndex, values.genderData]);

  const isHighToError = useMemo(() => {
    const aboveOptimalTo = values.genderData[dataIndex].above_optimal_to;
    const aboveOptimalFrom = values.genderData[dataIndex].above_optimal_from;
    return (
      aboveOptimalTo !== '' && aboveOptimalFrom !== '' && Number(aboveOptimalTo) <= Number(aboveOptimalFrom)
    );
  }, [dataIndex, values.genderData]);

  const isCriticalHighFromError = useMemo(() => {
    const highFrom = values.genderData[dataIndex].high_from;
    const aboveOptimalTo = values.genderData[dataIndex].above_optimal_to;
    return (
      highFrom !== '' &&
      aboveOptimalTo !== '' &&
      Number(highFrom) <= Number(aboveOptimalTo)
    );
  }, [dataIndex, values.genderData]);

  const isCriticalHighToError = useMemo(() => {
    const highTo = values.genderData[dataIndex].high_to;
    const highFrom = values.genderData[dataIndex].high_from;
    return (
      highTo !== '' &&
      highTo !== '∞' &&
      highFrom !== '' &&
      Number(highTo) <= Number(highFrom)
    );
  }, [dataIndex, values.genderData]);

  return (
    <div className='gender-values-block'>
      <Select
        title='Gender *'
        placeholder='All genders'
        isError={false}
        name='gender'
        selectedOption={values.genderData[dataIndex].gender}
        onChange={(option) =>
          setFieldValue(`genderData[${dataIndex}].gender`, option)
        }
        options={
          dataIndex === 0
            ? [
              { key: UserGenderEnum.Male, value: 'Male' },
              { key: UserGenderEnum.Female, value: 'Female' },
              { key: 'all', value: 'All' }
            ]
            : [
              { key: UserGenderEnum.Male, value: 'Male' },
              { key: UserGenderEnum.Female, value: 'Female' }
            ]
        }
      />
      {!values.age_dependent ? (
        <div className='critical-values-container'>
          <div className='values-row'>
            <div style={{ width: '100%' }}>
              <div className='input-label'>
                Low *
                <label>
                  <input
                    type='checkbox'
                    checked={[
                      values.genderData[dataIndex].low_from,
                      values.genderData[dataIndex].low_to
                    ].includes('NA')}
                    disabled={false}
                    onChange={(e) =>
                      onDisableRange(e, [
                        'low_from',
                        'low_to'
                      ])
                    }
                  />
                  <span>Disable</span>
                </label>
              </div>
              <TextInput
                name='low_from'
                placeholder={
                  values.genderData[dataIndex].low_from === 'NA'
                    ? 'NA'
                    : 'from'
                }
                isDisabled={
                  values.genderData[dataIndex].low_from === 'NA'
                }
                type='number'
                value={
                  values.genderData[dataIndex].low_from !== 'NA'
                    ? values.genderData[dataIndex].low_from
                    : ''
                }
                onChange={onCriticalLowFromChange}
                isError={isCriticalLowFromError}
              />
            </div>
            <span className='critical-values-separator'>-</span>
            <div style={{ width: '100%' }}>
              <div className="input-label" >
                <label className='d-flex align-items-center gap-2'>
                  <input
                    type='checkbox'
                    checked={isInfiniteLowTo}
                    onChange={onInfiniteLowToChange}
                    disabled={values.genderData[dataIndex].low_from === 'NA' && values.genderData[dataIndex].low_to === 'NA'}
                  />
                  <span>Greater than</span>
                </label>
              </div>
              <TextInput
                name='low_to'
                placeholder={
                  values.genderData[dataIndex].low_to === 'NA'
                    ? 'NA'
                    : isInfiniteLowTo
                      ? '∞'
                      : 'to'
                }
                isDisabled={values.genderData[dataIndex].low_to === 'NA' || isInfiniteLowTo}
                type='number'
                onChange={onCriticalLowToChange}
                value={
                  values.genderData[dataIndex].low_to !== 'NA'
                    ? values.genderData[dataIndex].low_to
                    : ''
                }
                isError={isCriticalLowToError}
              />
            </div>
          </div>
          <div className='values-row'>
            <div style={{ width: '100%' }}>
              <div className='input-label'>
                Below Optimal *
                <label>
                  <input
                    type='checkbox'
                    checked={[
                      values.genderData[dataIndex].below_optimal_from,
                      values.genderData[dataIndex].below_optimal_to
                    ].includes('NA')}
                    disabled={isInfiniteLowTo}
                    onChange={(e) => onDisableRange(e, ['below_optimal_from', 'below_optimal_to'])}
                  />
                  <span>Disable</span>
                </label>
              </div>
              <TextInput
                name='below_optimal_from'
                placeholder={
                  values.genderData[dataIndex].below_optimal_from === 'NA' ? 'NA' : 'from'
                }
                type='number'
                isDisabled={values.genderData[dataIndex].below_optimal_from === 'NA' || isInfiniteLowTo}
                onChange={onLowFromChange}
                value={
                  values.genderData[dataIndex].below_optimal_from !== 'NA'
                    ? values.genderData[dataIndex].below_optimal_from
                    : ''
                }
                isError={isLowFromError}
              />
            </div>
            <span className='critical-values-separator'>-</span>
            <div style={{ width: '100%' }}>
              <div className="input-label" >
                <label className='d-flex align-items-center gap-2'>
                  <input
                    type='checkbox'
                    checked={isInfiniteBelowOptimalTo}
                    onChange={onInfiniteBelowOptimalToChange}
                    disabled={(values.genderData[dataIndex].below_optimal_from === 'NA' && values.genderData[dataIndex].below_optimal_to === 'NA') || isInfiniteLowTo}
                  />
                  <span>Greater than</span>
                </label>
              </div>
              <TextInput
                name='below_optimal_to'
                placeholder={
                  values.genderData[dataIndex].below_optimal_from === 'NA' ? 'NA' : isInfiniteBelowOptimalTo ? '∞' : 'to'
                }
                type='number'
                isDisabled={values.genderData[dataIndex].below_optimal_to === 'NA' || isInfiniteBelowOptimalTo || isInfiniteLowTo}
                onChange={onLowToChange}
                value={
                  values.genderData[dataIndex].below_optimal_to !== 'NA'
                    ? values.genderData[dataIndex].below_optimal_to
                    : ''
                }
                isError={isLowToError}
              />
            </div>
          </div>
          <div className='values-row'>
            <div style={{ width: '100%' }}>
              <div className='input-label'>
                Optimal *
                <label>
                  <input
                    type='checkbox'
                    checked={[
                      values.genderData[dataIndex].optimal_from,
                      values.genderData[dataIndex].optimal_to
                    ].includes('NA')}
                    disabled={isInfiniteLowTo || isInfiniteBelowOptimalTo}
                    onChange={(e) =>
                      onDisableRange(e, ['optimal_from', 'optimal_to'])
                    }
                  />
                  <span>Disable</span>
                </label>
              </div>
              <TextInput
                name='optimal_from'
                placeholder={
                  values.genderData[dataIndex].optimal_from === 'NA'
                    ? 'NA'
                    : 'from'
                }
                type='number'
                isDisabled={values.genderData[dataIndex].optimal_from === 'NA' || isInfiniteLowTo || isInfiniteBelowOptimalTo}
                onChange={onOptimalFromChange}
                value={
                  values.genderData[dataIndex].optimal_from !== 'NA'
                    ? values.genderData[dataIndex].optimal_from
                    : ''
                }
                isError={isOptimalFromError}
              />
            </div>
            <span className='critical-values-separator'>-</span>
            <div style={{ width: '100%' }}>
              <div className="input-label" >
                <label className='d-flex align-items-center gap-2'>
                  <input
                    type='checkbox'
                    checked={isInfiniteOptimalTo}
                    onChange={onInfiniteOptimalToChange}
                    disabled={(values.genderData[dataIndex].optimal_from === 'NA' && values.genderData[dataIndex].optimal_to === 'NA') || isInfiniteLowTo || isInfiniteBelowOptimalTo}
                  />
                  <span>Greater than</span>
                </label>
              </div>
              <TextInput
                name='optimal_to'
                placeholder={
                  values.genderData[dataIndex].optimal_to === 'NA' ? 'NA' : isInfiniteOptimalTo ? '∞' : 'to'
                }
                type='number'
                isDisabled={values.genderData[dataIndex].optimal_to === 'NA' || isInfiniteOptimalTo || isInfiniteLowTo || isInfiniteBelowOptimalTo}
                onChange={onOptimalToChange}
                value={
                  values.genderData[dataIndex].optimal_to !== 'NA'
                    ? values.genderData[dataIndex].optimal_to
                    : ''
                }
                isError={isOptimalToError}
              />
            </div>
          </div>
          <div className='values-row'>
            <div style={{ width: '100%' }}>
              <div className='input-label'>
                Above Optimal *
                <label>
                  <input
                    type='checkbox'
                    checked={[
                      values.genderData[dataIndex].above_optimal_from,
                      values.genderData[dataIndex].above_optimal_to
                    ].includes('NA')}
                    disabled={isInfiniteLowTo || isInfiniteBelowOptimalTo || isInfiniteOptimalTo}
                    onChange={(e) =>
                      onDisableRange(e, ['above_optimal_from', 'above_optimal_to'])
                    }
                  />
                  <span>Disable</span>
                </label>
              </div>
              <TextInput
                name='above_optimal_from'
                placeholder={
                  values.genderData[dataIndex].above_optimal_from === 'NA'
                    ? 'NA'
                    : 'from'
                }
                type='number'
                isDisabled={values.genderData[dataIndex].above_optimal_from === 'NA' || isInfiniteLowTo || isInfiniteBelowOptimalTo || isInfiniteOptimalTo}
                onChange={onHighFromChange}
                value={
                  values.genderData[dataIndex].above_optimal_from !== 'NA'
                    ? values.genderData[dataIndex].above_optimal_from
                    : ''
                }
                isError={isHighFromError}
              />
            </div>
            <span className='critical-values-separator'>-</span>
            <div style={{ width: '100%' }}>
              <div className="input-label" >
                <label className='d-flex align-items-center gap-2'>
                  <input
                    type='checkbox'
                    checked={isInfiniteAboveOptimalTo}
                    onChange={onInfiniteAboveOptimalToChange}
                    disabled={(values.genderData[dataIndex].above_optimal_from === 'NA' && values.genderData[dataIndex].above_optimal_to === 'NA') || isInfiniteLowTo || isInfiniteBelowOptimalTo || isInfiniteOptimalTo}
                  />
                  <span>Greater than</span>
                </label>
              </div>
              <TextInput
                name='above_optimal_to'
                placeholder={
                  values.genderData[dataIndex].above_optimal_to === 'NA' ? 'NA' : isInfiniteAboveOptimalTo ? '∞' : 'to'
                }
                type='number'
                isDisabled={values.genderData[dataIndex].above_optimal_to === 'NA' || isInfiniteAboveOptimalTo || isInfiniteLowTo || isInfiniteBelowOptimalTo || isInfiniteOptimalTo}
                onChange={onHighToChange}
                value={
                  values.genderData[dataIndex].above_optimal_to !== 'NA'
                    ? values.genderData[dataIndex].above_optimal_to
                    : ''
                }
                isError={isHighToError}
              />
            </div>
          </div>
          <div className='values-row'>
            <div style={{ width: '100%' }}>
              <div className='input-label'>
                High *
                <label>
                  <input
                    type='checkbox'
                    checked={
                      values.genderData[dataIndex].high_from === 'NA' &&
                      values.genderData[dataIndex].high_to === 'NA'
                    }
                    disabled={isInfiniteLowTo || isInfiniteBelowOptimalTo || isInfiniteOptimalTo || isInfiniteAboveOptimalTo}
                    onChange={(e) =>
                      onDisableRange(e, [
                        'high_from',
                        'high_to'
                      ])
                    }
                  />
                  <span>Disable</span>
                </label>
              </div>
              <TextInput
                name='high_from'
                placeholder={
                  values.genderData[dataIndex].high_from === 'NA'
                    ? 'NA'
                    : 'from'
                }
                type='number'
                isDisabled={
                  values.genderData[dataIndex].high_from === 'NA' || isInfiniteLowTo || isInfiniteBelowOptimalTo || isInfiniteOptimalTo || isInfiniteAboveOptimalTo
                }
                onChange={onCriticalHighFromChange}
                value={
                  values.genderData[dataIndex].high_from !== 'NA'
                    ? values.genderData[dataIndex].high_from
                    : ''
                }
                isError={isCriticalHighFromError}
              />
            </div>
            <span className='critical-values-separator'>-</span>
            <div style={{ width: '100%' }}>
              <div className="input-label" >
                <label className='d-flex align-items-center gap-2'>
                  <input
                    type='checkbox'
                    checked={isInfiniteHighTo}
                    onChange={onInfiniteHighToChange}
                    disabled={(values.genderData[dataIndex].high_from === 'NA' && values.genderData[dataIndex].high_to === 'NA') || isInfiniteLowTo || isInfiniteBelowOptimalTo || isInfiniteOptimalTo || isInfiniteAboveOptimalTo}
                  />
                  <span>Greater than</span>
                </label>
              </div>
              <TextInput
                name='high_to'
                placeholder={
                  values.genderData[dataIndex].high_to === 'NA'
                    ? 'NA'
                    : isInfiniteHighTo
                      ? '∞'
                      : 'to'
                }
                type='number'
                isDisabled={
                  values.genderData[dataIndex].high_to === 'NA' || isInfiniteHighTo || isInfiniteLowTo || isInfiniteBelowOptimalTo || isInfiniteOptimalTo || isInfiniteAboveOptimalTo
                }
                onChange={onCriticalHighToChange}
                value={
                  values.genderData[dataIndex].high_to !== 'NA'
                    ? values.genderData[dataIndex].high_to
                    : ''
                }
                isError={isCriticalHighToError}
              />
            </div>
          </div>
        </div>
      ) : (
        values.genderData[dataIndex].data_with_age.map(
          (age: any, ageIndex: number) => (
            <AgesGenderValuesBlock
              key={`age-${ageIndex}`}
              dataIndex={dataIndex}
              itemIndex={ageIndex}
            />
          )
        )
      )}
      {values.age_dependent && (
        <div>
          <div className='add-block-btn' onClick={addAgeBlock}>
            <PlusIcon />
            <span>Add Age Range</span>
          </div>
        </div>
      )}
      {values.genderData.length !== 1 && (
        <Button text='Delete block' onClick={onDeleteGenderBlock} />
      )}
      {dataIndex === values.genderData.length - 1 && (
        <Button
          text='Add values for another gender'
          onClick={onAddGenderBlock}
          isDisabled={
            values.genderData.length >= 2 ||
            values.genderData[dataIndex]?.gender?.key === 'all'
          }
        />
      )}
    </div>
  );
};

export default GenderValuesBlock;
